import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  DbOntologyView,
  useCreateDbOntologyView,
  useUpdateDbOntologyNode,
} from "@/service-hooks/use-get-db-ontology-service";

interface DbOntologyViewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  dataLakeLabel?: string;
  view?: DbOntologyView | null;
  onSaved?: () => void;
  // Viewer-role opens the same dialog to inspect details; all inputs are
  // read-only and the save button is hidden. See the parent tab layout.
  isReadOnly?: boolean;
}

interface FormState {
  name: string;
  schema: string;
  viewType: "view" | "materialized_view";
  definition: string;
  comment: string;
  columns: string;
}

const EMPTY: FormState = {
  name: "",
  schema: "",
  viewType: "view",
  definition: "",
  comment: "",
  columns: "",
};

// Accept JSON array or comma/newline-separated names. Returns [] when empty.
const parseColumns = (raw: string): string[] => {
  const text = raw.trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.map((p) => (typeof p === "string" ? p : String(p))).filter(Boolean);
    }
  } catch {
    // fall through
  }
  return text
    .split(/[\n,]/g)
    .map((p) => p.trim())
    .filter(Boolean);
};

export default function DbOntologyViewDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  dataLakeLabel,
  view,
  onSaved,
  isReadOnly = false,
}: DbOntologyViewDialogProps) {
  const isEdit = !!view?.id;
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createView = useCreateDbOntologyView();
  const updateNode = useUpdateDbOntologyNode();

  useEffect(() => {
    if (!isOpen) return;
    if (view) {
      const vt = (view.viewType as FormState["viewType"]) || "view";
      setForm({
        name: view.name || "",
        schema: view.schema || "",
        viewType: vt === "materialized_view" ? "materialized_view" : "view",
        definition: view.definition || "",
        comment: view.description || "",
        columns: Array.isArray(view.columns) ? view.columns.join(", ") : "",
      });
    } else {
      setForm({ ...EMPTY });
    }
  }, [isOpen, view]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    const columns = parseColumns(form.columns);
    try {
      if (isEdit && view?.id) {
        await updateNode.mutateAsync({
          label: "DDLView",
          projectUuid,
          id: view.id,
          dataLakeId,
          data: {
            name: form.name.trim(),
            ...(form.schema.trim() ? { schema: form.schema.trim() } : {}),
            viewType: form.viewType,
            ...(form.definition.trim() ? { definition: form.definition.trim() } : {}),
            ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
            columns,
          },
        });
      } else {
        await createView.mutateAsync({
          projectUuid,
          dataLakeId,
          name: form.name.trim(),
          schema: form.schema.trim() || undefined,
          viewType: form.viewType,
          definition: form.definition.trim() || undefined,
          comment: form.comment.trim() || undefined,
          columns,
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  const isPending = createView.isPending || updateNode.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isReadOnly ? "View" : isEdit ? "Edit View" : "Create View"}
            {dataLakeLabel ? ` — ${dataLakeLabel}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the view fields for the selected data lake.
          </DialogDescription>
        </DialogHeader>

        {/* pl-1 balances pr-1 so the focus ring on the leftmost input edge
            isn't clipped by the parent's `overflow-auto` (CUSTOM_SCROLLBAR). */}
        <div className={`space-y-4 overflow-y-auto flex-1 pl-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Enter view name"
              readOnly={isReadOnly}
              className="bg-surface text-white px-4"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Schema</label>
              <Input
                value={form.schema}
                onChange={(e) => update("schema", e.target.value)}
                placeholder="e.g. public"
                readOnly={isReadOnly}
                className="bg-surface text-white px-4"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">View Type</label>
              <select
                value={form.viewType}
                onChange={(e) =>
                  update("viewType", e.target.value as FormState["viewType"])
                }
                disabled={isReadOnly}
                className="w-full h-10 rounded-md border border-input bg-surface text-white px-4 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="view">view</option>
                <option value="materialized_view">materialized_view</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Columns</label>
            <Textarea
              value={form.columns}
              onChange={(e) => update("columns", e.target.value)}
              placeholder='Comma/newline separated, or JSON array (e.g. ["id", "name"])'
              readOnly={isReadOnly}
              className="bg-surface text-white px-4 min-h-[60px] font-mono text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Comment</label>
            <Textarea
              value={form.comment}
              onChange={(e) => update("comment", e.target.value)}
              placeholder="Optional description for this view"
              readOnly={isReadOnly}
              className="bg-surface text-white px-4 min-h-[60px]"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Definition (SQL)</label>
            <Textarea
              value={form.definition}
              onChange={(e) => update("definition", e.target.value)}
              placeholder="SELECT ... FROM ..."
              readOnly={isReadOnly}
              className="bg-surface text-white px-4 min-h-[120px] font-mono text-xs"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSubmit}
              disabled={!form.name.trim() || isPending}
              className="bg-primary hover:bg-primary-dark text-white"
            >
              {isPending ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
